<template>
  <div class="kanban-board">
    <KanbanColumn
      v-for="column in columns"
      :key="column.column_id"
      :column="column"
      :base-url="baseUrl"
      @load-more="handleLoadMore"
      @set-drag-event="setDragEvent"
      @update:revert-drag="handleUndo"
      @update:column-totals="updateTotals"
    />
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import KanbanColumn from '@/components/shared/Kanban/KanbanColumn.vue';
import type { KanbanColumnData, FetchParams } from '@/types/kanban';
import type { DragEndEvent } from '@/types/draggable';
import { toast } from 'vue-sonner';

const { columns, baseUrl } = defineProps<{
  columns: KanbanColumnData[];
  baseUrl: string;
}>();

const emit = defineEmits(['load-more']);

const dragEvent = ref<DragEndEvent | null>(null);

function setDragEvent(event: DragEndEvent | null) {
  dragEvent.value = event;
}

function handleLoadMore(params: FetchParams) {
  emit('load-more', params);
}

function updateTotals(totals: { [key: string]: number }) {
  columns.forEach(column => {
    column.pagination.total = totals[column.column_id] || 0;
  });
}

function handleUndo(message?: string) {
  if (dragEvent.value) {
    const currentColumnId = dragEvent.value.to.id;
    const currentColumn = columns.find(col => col.column_id === currentColumnId);
    const currentIndex = dragEvent.value.newIndex;

    const previousColumnId = dragEvent.value.from.id;
    const previousColumn = columns.find(col => col.column_id === previousColumnId);
    const previousIndex = dragEvent.value.oldIndex;

    const item = currentColumn!.tasks.splice(currentIndex, 1)[0];
    previousColumn!.tasks.splice(previousIndex, 0, item);

    setDragEvent(null);
    if (message) {
      toast.error(message);
    }
  }
}
</script>

<style scoped>
@reference "tailwindcss";

.kanban-board {
  @apply flex flex-row gap-4 overflow-x-auto bg-gray-100 pt-4 pb-0 rounded-lg;
}
</style>
